import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable, take } from 'rxjs';
import { HashMap, LocalizationService } from '@capital-access/common/localization';
import { CommonLocalizationModule } from '@capital-access/common/localization';
import { getAccount, getUser, ImpersonationStopReason, stopImpersonation } from '@capital-access/common/user';
import { Alert, ConfirmationModalOptions, FireflyModalService, ModalRef } from '@capital-access/firefly/components';
import { FireflyAlertsModule } from '@capital-access/firefly/components';
import { NAVIGATION_LOCALIZATION_SCOPE } from '../navigation.constants';

@Component({
  selector: 'ca-impersonation-banner',
  standalone: true,
  imports: [AsyncPipe, FireflyAlertsModule, CommonLocalizationModule],
  templateUrl: './impersonation-banner.component.html',
  styleUrls: ['./impersonation-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImpersonationBannerComponent {
  Alert = Alert;
  localizationScope = NAVIGATION_LOCALIZATION_SCOPE;

  private store = inject(Store);
  private localizationService = inject(LocalizationService);
  private modalService = inject(FireflyModalService);
  private modalRef: ModalRef<ConfirmationModalOptions> | null = null;

  userName$ = this.store.select(getUser).pipe(
    map(user => {
      const firstName = user?.firstName?.trim() || '';
      const lastName = user?.lastName?.trim() || '';
      return lastName ? `${firstName} ${lastName}` : firstName;
    })
  );

  accountName$ = this.store.select(getAccount).pipe(map(account => account!.name));

  exitImpersonation(): void {
    this.getLocalization().subscribe(localization => {
      this.modalRef = this.modalService.openConfirmation({
        style: 'warning',
        title: localization.exitImpersonationConfirmTitle,
        body: localization.exitImpersonationConfirmMessage,
        actionText: localization.exit,
        dismissText: localization.cancel
      });

      this.modalRef.result
        .then(() => {
          this.store.dispatch(stopImpersonation({ reason: ImpersonationStopReason.UserInitiated }));
        })
        .finally(() => {
          this.modalRef = null;
        });
    });
  }

  private getLocalization(): Observable<HashMap> {
    return this.localizationService.getLocalization(this.localizationScope).pipe(take(1));
  }
}
